import React, { useCallback, useRef, useState } from 'react';
import { Map } from 'immutable';

import { WysiwygField, useWindowDimensions } from '@uncinc/uncinc-react-kitchen-sink';

import Truncate from 'react-truncate';
import { useTranslation } from 'react-i18next';
import {
  Container,
  CoreLink,
} from '..';
import { IS_CLIENT, SCREEN_NORMAL } from '../../config/constants';
import { calculateDistanceToScreenBorder } from '../../helpers';
import DynamicMediaItem from '../DynamicMediaItem';

import './style.scss';

const MOBILE_CARD_CHANGE_OFFSET = 160;
const DESKTOP_CARD_CHANGE_OFFSET = 240;


const ContentSlider = ({ entities }) => {
  const { t } = useTranslation();
  // slider base
  const sliderRef = useRef();
  const [sliderIsAtStart, setSliderIsAtStart] = useState(true);
  const [sliderIsAtEnd, setSliderIsAtEnd] = useState(false);
  const [sliderIsMoving, setSliderIsMoving] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const { width } = useWindowDimensions();
  const mobileTablet = width < SCREEN_NORMAL;
  const scrollOffset = mobileTablet ? 315 : 480;

  if (IS_CLIENT) {
    document.documentElement.style.setProperty('--scrollbar-width', `${window.innerWidth - document.documentElement.clientWidth}px`);
  }

  const updateSliderStatus = useCallback(() => {
    // Moving the slider while it was already moving messed up the positioning.
    // this prevents that.
    setSliderIsMoving(true);
    setTimeout(() => {
      setSliderIsAtStart(sliderRef.current.scrollLeft === 0);
      // this does not work well currently
      setSliderIsAtEnd(sliderRef.current.scrollLeft === sliderRef.current.scrollWidth);
      setSliderIsMoving(false);
    }, 700);
  }, [sliderRef, setSliderIsMoving, setSliderIsAtStart, setSliderIsAtEnd]);

  const slideBackwards = () => {
    if (sliderIsMoving) {
      return;
    }
    sliderRef.current.scrollLeft = sliderRef.current.scrollLeft - scrollOffset;
    updateSliderStatus();
  };

  const slideToIndex = useCallback((index) => {
    if (sliderIsMoving) {
      return;
    }
    sliderRef.current.scrollLeft = index * scrollOffset;
    updateSliderStatus();
  }, [scrollOffset, sliderIsMoving, sliderRef]);

  const slideForwards = () => {
    if (sliderIsMoving) {
      return;
    }
    sliderRef.current.scrollLeft = sliderRef.current.scrollLeft + scrollOffset;
    updateSliderStatus();
  };

  // staticCard on top functionality
  const [, setRerender] = useState(0);
  const [staticCard, setStaticCard] = useState(Map());

  const itemsRef = useRef({});

  // get position of left border which cards will be passing through
  const handleScroll = () => {
    setRerender((prev) => prev + 1);
  };

  // on every ref change this function is called
  const handlePosition = (position, index) => {
    // position correct for padding left
    const distanceToScreenBorder = calculateDistanceToScreenBorder(width);
    const correction = (mobileTablet ? MOBILE_CARD_CHANGE_OFFSET : DESKTOP_CARD_CHANGE_OFFSET) - distanceToScreenBorder;

    // keep in ref array of elements with their updated position so we can decide which card to show
    itemsRef.current[index.toString()] = position + correction;

    // get a card that is 'active', thus the offset left is bigger then 0, but smallest from the rest
    const smallestOverZero = Object.values(itemsRef.current)
      .filter((num) => num >= 0) // filter out non-positive numbers
      .reduce((acc, cur) => Math.min(acc, cur), Infinity); // reduce to find smallest element

    // find this value in the object of refs
    const key = Object.keys(itemsRef.current).find((findKey) => itemsRef.current[findKey] === smallestOverZero);
    setActiveIndex(Number(key));
    setStaticCard(entities.get(key) || Map());
  };

  let staticCardBody;
  let staticCardTitle;
  let linkUri;
  let linkTitle;
  if (staticCard) {
    staticCardBody = staticCard.getIn(['entity', 'field_body', 0, 'value'], '');
    staticCardTitle = staticCard.getIn(['entity', 'field_title', 0, 'value'], '');
    linkUri = staticCard.getIn(['entity', 'field_link', 0, 'alias'], staticCard.getIn(['entity', 'field_link', 0, 'uri']));
    linkTitle = staticCard.getIn(['entity', 'field_link', 0, 'title'], t('show_campaign'));
  }

  return (
    <div className="ContentSlider">
      <div className="ContentSlider__inner">
        <div className="ContentSlider__inner__content">
          <div className="container Container">

            {/* static card */}
            {staticCard.size > 0 && (
              <div className="ContentSlider__staticCard">
                <CoreLink to={linkUri}>
                  <h3>{staticCardTitle}</h3>
                  {staticCardBody && (
                    <div className="ContentSlider__staticCard__description">
                      <Truncate lines={3} ellipsis="...">
                        <WysiwygField
                          property="schema:text"
                          html={staticCardBody}
                        />
                      </Truncate>
                    </div>
                  )}
                  <div className="ContentSlider__staticCard__link">
                    <i className="icon-after icon-before icon-arrow-right icon-variant-4 moving-arrow" />
                    <span>{linkTitle}</span>
                  </div>
                </CoreLink>
              </div>
            )}
          </div>

          {/* slider */}
          <div className="ContentSlider__inner__content__slider" ref={sliderRef} onScroll={handleScroll}>
            {entities && entities.map((entity, index) => {
              if (entity) {
                const paragraphId = entity.get('target_id');
                const paragraphKey = `paragraph:${paragraphId}`;
                const title = entity.getIn(['entity', 'field_title', 0, 'value'], '');
                const image = entity.getIn(['entity', 'field_images', 0], null);
                const uuid = entity.get('target_uuid', '');
                const isActive = uuid === staticCard.get('target_uuid', '');
                const link = entity.getIn(['entity', 'field_link', 0, 'alias'], entity.getIn(['entity', 'field_link', 0, 'uri']));
                const isBlocked = activeIndex !== index || sliderIsMoving;
                const onClick = activeIndex !== index && !sliderIsMoving ? () => { slideToIndex(index) } : null;
                return (
                  <div
                    id={paragraphKey}
                    key={uuid}
                    className={`ContentSlider__inner__content__slider__item ${isActive ? 'active' : ''}`}
                    ref={(el) => {
                      if (!el) return;
                      handlePosition(el.getBoundingClientRect().left, index);
                    }}>

                    <CoreLink
                      to={link}
                      blocked={isBlocked}
                      onClick={onClick}
                    >
                      {image && (
                        <DynamicMediaItem
                          media={image.toJS()}
                          imageAspectRatio="3_4"
                        />
                      )}
                      <div className="overlay" />
                      <div className="wrapper">
                        <h2>{title}</h2>
                      </div>
                    </CoreLink>
                  </div>
                );
              }
            })}
          </div>
        </div>

        {/* navigation */}
        <Container>
          <div className="ContentSlider__navigation">


            <div className={`ContentSlider__navigation__prev ${sliderIsAtStart ? 'disabled' : ''}`} onClick={slideBackwards}>
              <div className="icon icon-arrow-left" />
            </div>


            <div className={`ContentSlider__navigation__next ${sliderIsAtEnd ? 'disabled' : ''}`} onClick={slideForwards}>
              <div className="icon icon-arrow-right" />
            </div>

          </div>
        </Container>

      </div>
    </div>
  );
};

export default ContentSlider;
